import { createFileRoute, Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Card, CardBody, CardHeader } from '../components/Card.js'
import { Section } from '../components/Section.js'
import { Button } from '../components/Button.js'
import { Field, Select, Checkbox, Input } from '../components/Field.js'
import { StatusPill } from '../components/StatusPill.js'
import { Icon } from '../components/Icon.js'

export const Route = createFileRoute('/_app/super/academies/$id')({
  component: SuperAcademyDetailPage,
})

interface AcademyDetail {
  id: string
  name: string
  slug: string
  email: string
  phone: string | null
  city: string | null
  state: string | null
  status: 'trial' | 'active' | 'suspended' | 'cancelled'
  planId: string | null
  planOverride: Record<string, boolean> | null
  trialEndsAt: string | null
  suspendedAt: string | null
  createdAt: string
}

interface PlanRow {
  id: string
  name: string
  status: 'published' | 'unpublished' | 'deprecated'
  features: Record<string, boolean>
}

const STATUS_OPTIONS: AcademyDetail['status'][] = [
  'trial',
  'active',
  'suspended',
  'cancelled',
]

const FEATURE_KEYS = [
  'fee_collection',
  'attendance',
  'biometric_sync',
  'performance',
  'exam_boards',
  'parent_communication',
  'referrals',
  'white_label',
  'social_login',
  'teacher_accounts',
  'email_customisation',
  'reporting',
  'whatsapp_notifications',
] as const

function SuperAcademyDetailPage() {
  const { id } = Route.useParams()
  const qc = useQueryClient()

  const academy = useQuery({
    queryKey: ['super', 'academy', id],
    queryFn: async () => {
      const res = await api<AcademyDetail>(`/api/v1/super/academies/${id}`)
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const plans = useQuery({
    queryKey: ['super', 'plans'],
    queryFn: async () => {
      const res = await api<PlanRow[]>('/api/v1/super/plans')
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
  })

  const [planSelection, setPlanSelection] = useState<string>('')
  const [statusSelection, setStatusSelection] = useState<AcademyDetail['status']>('trial')
  const [overrideDraft, setOverrideDraft] = useState<Record<string, boolean>>({})
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  useEffect(() => {
    if (!academy.data) return
    setPlanSelection(academy.data.planId ?? '')
    setStatusSelection(academy.data.status)
    setOverrideDraft({ ...(academy.data.planOverride ?? {}) })
  }, [academy.data])

  const planById = (id: string | null) =>
    id ? plans.data?.find((p) => p.id === id) : undefined
  const currentPlan = planById(academy.data?.planId ?? null)
  const planFeatures = currentPlan?.features ?? {}

  // All three mutations write the server-returned academy row straight into the
  // detail cache instead of refetching. Hyperdrive caching is disabled, but
  // trusting the response also avoids a redundant round-trip and keeps the UI
  // in sync with exactly what the server persisted.
  const assignPlan = useMutation({
    mutationFn: async () => {
      const res = await api<AcademyDetail>(`/api/v1/super/academies/${id}/plan`, {
        method: 'PATCH',
        body: JSON.stringify({ planId: planSelection || null }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (updated) => {
      qc.setQueryData<AcademyDetail>(['super', 'academy', id], updated)
      qc.invalidateQueries({ queryKey: ['super', 'academies'] })
      setSaved('Plan updated')
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const setStatus = useMutation({
    mutationFn: async () => {
      const res = await api<AcademyDetail>(`/api/v1/super/academies/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: statusSelection, reason: reason || undefined }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (updated) => {
      qc.setQueryData<AcademyDetail>(['super', 'academy', id], updated)
      qc.invalidateQueries({ queryKey: ['super', 'academies'] })
      setSaved('Status updated')
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  const updateOverrides = useMutation({
    mutationFn: async () => {
      const res = await api<AcademyDetail>(`/api/v1/super/academies/${id}/features`, {
        method: 'PATCH',
        body: JSON.stringify({ features: overrideDraft, reason: reason || undefined }),
      })
      if ('error' in res) throw new Error(res.error.message)
      return res.data
    },
    onSuccess: (updated) => {
      qc.setQueryData<AcademyDetail>(['super', 'academy', id], updated)
      setSaved('Feature overrides updated')
      setError(null)
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  if (academy.isLoading) {
    return <p className="text-sm text-fg-subtle">Loading…</p>
  }
  if (academy.error || !academy.data) {
    return (
      <p className="text-sm text-danger">
        {academy.error instanceof Error ? academy.error.message : 'Failed to load'}
      </p>
    )
  }

  const a = academy.data

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader
        title={a.name}
        description={
          <>
            <Link to="/super/academies" className="hover:text-fg">
              ← Academies
            </Link>
            {' · '}
            <span className="font-mono">{a.slug}</span>
            {' · '}
            <StatusPill tone={a.status}>{a.status}</StatusPill>
          </>
        }
      />

      {error || saved ? (
        <div
          className={`flex items-start gap-2 rounded-md border px-4 py-3 text-sm ${
            error
              ? 'border-danger-border bg-danger-tint text-danger'
              : 'border-success-border bg-success-tint text-success'
          }`}
        >
          <Icon name={error ? 'alert' : 'check'} size={16} className="mt-0.5" />
          <p>{error ?? saved}</p>
        </div>
      ) : null}

      <Section title="Plan">
        <Card>
          <CardBody className="space-y-4">
            <Field label="Assigned plan">
              <Select value={planSelection} onChange={(e) => setPlanSelection(e.target.value)}>
                <option value="">No plan</option>
                {(plans.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} {p.status !== 'published' ? `(${p.status})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
          </CardBody>
          <div className="border-t border-border bg-surface-muted px-5 py-3 flex justify-end">
            <Button onClick={() => assignPlan.mutate()} loading={assignPlan.isPending}>
              Save plan
            </Button>
          </div>
        </Card>
      </Section>

      <Section title="Status">
        <Card>
          <CardBody className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Status">
                <Select
                  value={statusSelection}
                  onChange={(e) =>
                    setStatusSelection(e.target.value as AcademyDetail['status'])
                  }
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s} className="capitalize">
                      {s}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Reason (audit log)" optional>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. churned customer"
                />
              </Field>
            </div>
          </CardBody>
          <div className="border-t border-border bg-surface-muted px-5 py-3 flex justify-end">
            <Button
              onClick={() => setStatus.mutate()}
              loading={setStatus.isPending}
              variant={statusSelection === 'suspended' || statusSelection === 'cancelled' ? 'danger' : 'primary'}
            >
              Update status
            </Button>
          </div>
        </Card>
      </Section>

      <Section
        title="Feature overrides"
        description="Per-academy toggles applied on top of the assigned plan. Audited automatically."
      >
        <Card>
          <CardHeader title="Features" description={currentPlan ? `Plan: ${currentPlan.name}` : 'No plan assigned'} />
          <CardBody className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {FEATURE_KEYS.map((key) => {
              const planValue = !!planFeatures[key]
              const overrideValue = overrideDraft[key]
              const checked = overrideValue ?? planValue
              return (
                <div key={key} className="flex items-center justify-between py-1">
                  <Checkbox
                    label={
                      <span className="flex items-center gap-2">
                        <span className="font-mono text-xs">{key}</span>
                        {overrideValue !== undefined ? (
                          <span className="text-[10px] uppercase text-warning">override</span>
                        ) : (
                          <span className="text-[10px] uppercase text-fg-subtle">plan</span>
                        )}
                      </span>
                    }
                    checked={checked}
                    onChange={(next) =>
                      setOverrideDraft((curr) => ({ ...curr, [key]: next }))
                    }
                  />
                </div>
              )
            })}
          </CardBody>
          <div className="border-t border-border bg-surface-muted px-5 py-3 flex justify-end">
            <Button
              onClick={() => updateOverrides.mutate()}
              loading={updateOverrides.isPending}
            >
              Save overrides
            </Button>
          </div>
        </Card>
      </Section>
    </div>
  )
}
